import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Button, InputField } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';

interface RegisterProps {
  onRegister: (user: any) => void;
  onSwitchToLogin: () => void;
}

const ROLES = ['AGENT', 'MANAGER', 'BROKER'] as const;

const Register: React.FC<RegisterProps> = ({ onRegister, onSwitchToLogin }) => {
  const { addToast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '', role: 'AGENT' });
  const [loading, setLoading] = useState(false);

  const handleRegister = async () => {
    if (!form.name || !form.email || !form.password || !form.confirm) {
      addToast('error', 'Validation Error', 'All required fields must be filled.');
      return;
    }
    if (form.password !== form.confirm) {
      addToast('error', 'Mismatch', 'Passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      addToast('error', 'Too Short', 'Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await api.auth.register({ email: form.email, password: form.password, name: form.name, role: form.role });
      const loginData = await api.auth.login(form.email, form.password);
      const user = { ...loginData.user, token: loginData.token };
      onRegister(user);
      addToast('success', 'Account Created', `Welcome, ${form.name}!`);
    } catch (err: any) {
      addToast('error', 'Registration Failed', err.message || 'Could not create account');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRegister();
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--dark-950)',
      backgroundImage: 'radial-gradient(ellipse at 20% 0%, rgba(13,148,136,0.10) 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(13,148,136,0.06) 0%, transparent 50%)',
      padding: '20px',
    }}>
      <div style={{
        width: '100%', maxWidth: '480px',
        background: 'rgba(15,23,42,0.70)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-xl)',
        padding: '40px 36px', boxShadow: '0 24px 64px rgba(0,0,0,0.40)', animation: 'modalIn 0.4s ease',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '16px', margin: '0 auto 16px',
            background: 'rgba(13,148,136,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon icon="solar:map-point-bold-duotone" width={32} style={{ color: 'var(--primary-500)' }} />
          </div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', fontWeight: 700, color: 'var(--primary-600)', margin: '0 0 4px' }}>Create Account</h1>
          <p style={{ color: 'var(--dark-500)', fontSize: '13px', margin: 0 }}>Join VOP CRM</p>
        </div>

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }} onKeyDown={handleKeyDown}>
          <InputField label="Full Name *" placeholder="John Doe" icon="solar:user-bold-duotone"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <InputField label="Email *" type="email" placeholder="you@company.com" icon="solar:letter-bold-duotone"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <InputField label="Phone" placeholder="+63 9xx xxx xxxx" icon="solar:phone-bold-duotone"
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dark-400)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              style={{ background: 'rgba(15,23,42,0.50)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--dark-200)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <InputField label="Password *" type="password" placeholder="Min. 8 characters"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <InputField label="Confirm Password *" type="password" placeholder="Re-enter password"
              value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
          </div>

          <Button variant="primary" fullWidth loading={loading} onClick={handleRegister}
            style={{ marginTop: '8px', padding: '12px 20px' }}>
            Create Account
          </Button>
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0' }}>
          <div style={{ flex: 1, height: '1px', background: 'var(--glass-border)' }} />
          <span style={{ color: 'var(--dark-600)', fontSize: '12px' }}>OR</span>
          <div style={{ flex: 1, height: '1px', background: 'var(--glass-border)' }} />
        </div>

        <div style={{ textAlign: 'center' }}>
          <span style={{ color: 'var(--dark-400)', fontSize: '14px' }}>Already have an account? </span>
          <button onClick={onSwitchToLogin} style={{
            background: 'none', border: 'none', color: 'var(--primary-500)', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}>
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default Register;
